import React from 'react';

const LudoBoard = ({ gameState, playerColor, currentPlayer, possibleMoves, diceValue, onMovePiece, onRollDice }) => {
  const boardSize = 15;
  
  // Create the Ludo board layout
  const createBoard = () => {
    const board = [];
    for (let row = 0; row < boardSize; row++) {
      const rowData = [];
      for (let col = 0; col < boardSize; col++) {
        rowData.push(getCellInfo(row, col));
      }
      board.push(rowData);
    }
    return board;
  };

  const getCellInfo = (row, col) => {
    // Home areas (corners)
    if (row < 6 && col < 6) return { type: 'home', color: 'red' };
    if (row < 6 && col > 8) return { type: 'home', color: 'blue' };
    if (row > 8 && col < 6) return { type: 'home', color: 'green' };
    if (row > 8 && col > 8) return { type: 'home', color: 'yellow' };
    
    // Center area
    if (row >= 6 && row <= 8 && col >= 6 && col <= 8) return { type: 'center' };
    
    // Path squares
    if (row === 6 || row === 8 || col === 6 || col === 8) {
      return { type: 'path' };
    }
    
    // Safe zones (colored triangular areas leading to center)
    if (row === 7 && col < 6) return { type: 'safe', color: 'red' };
    if (col === 7 && row < 6) return { type: 'safe', color: 'blue' };
    if (row === 7 && col > 8) return { type: 'safe', color: 'yellow' };
    if (col === 7 && row > 8) return { type: 'safe', color: 'green' };
    
    return { type: 'empty' };
  };

  const getPiecesAtPosition = (row, col) => {
    if (!gameState || !gameState.players) return [];
    
    const pieces = [];
    Object.values(gameState.players).forEach(player => {
      if (player.pieces) {
        player.pieces.forEach(piece => {
          const piecePos = getPiecePosition(piece);
          if (piecePos.row === row && piecePos.col === col) {
            pieces.push({ ...piece, playerColor: player.color });
          }
        });
      }
    });
    return pieces;
  };

  const getPiecePosition = (piece) => {
    console.log(`Getting position for piece ${piece.id}:`, {
      isInHome: piece.isInHome,
      position: piece.position,
      color: piece.color
    });
    
    // Convert piece position to board coordinates
    // This is a simplified mapping - in a real implementation, 
    // you'd have a more complex mapping system
    if (piece.isInHome) {
      // Place pieces in home area based on color
      const homePositions = {
        red: [{ row: 1, col: 1 }, { row: 1, col: 4 }, { row: 4, col: 1 }, { row: 4, col: 4 }],
        blue: [{ row: 1, col: 10 }, { row: 1, col: 13 }, { row: 4, col: 10 }, { row: 4, col: 13 }],
        green: [{ row: 10, col: 1 }, { row: 10, col: 4 }, { row: 13, col: 1 }, { row: 13, col: 4 }],
        yellow: [{ row: 10, col: 10 }, { row: 10, col: 13 }, { row: 13, col: 10 }, { row: 13, col: 13 }]
      };
      const positions = homePositions[piece.color] || [{ row: 0, col: 0 }];
      const index = parseInt(piece.id.split('_')[1]) - 1;
      const homePos = positions[index] || { row: 0, col: 0 };
      console.log(`Piece ${piece.id} in home at:`, homePos);
      return homePos;
    }
    
    // Map position number to board coordinates for path
    const coords = mapPositionToCoordinates(piece.position);
    console.log(`Piece ${piece.id} at position ${piece.position} mapped to:`, coords);
    return coords;
  };

  const mapPositionToCoordinates = (position) => {
    // Complete Ludo board path mapping (52 positions)
    const pathCoordinates = [
      // Red start and path (positions 0-12)
      { row: 6, col: 1 }, { row: 6, col: 2 }, { row: 6, col: 3 },
      { row: 6, col: 4 }, { row: 6, col: 5 }, { row: 5, col: 6 },
      { row: 4, col: 6 }, { row: 3, col: 6 }, { row: 2, col: 6 },
      { row: 1, col: 6 }, { row: 0, col: 6 }, { row: 0, col: 7 },
      { row: 0, col: 8 },
      
      // Blue start and path (positions 13-25)
      { row: 1, col: 8 }, { row: 2, col: 8 }, { row: 3, col: 8 },
      { row: 4, col: 8 }, { row: 5, col: 8 }, { row: 6, col: 9 },
      { row: 6, col: 10 }, { row: 6, col: 11 }, { row: 6, col: 12 },
      { row: 6, col: 13 }, { row: 6, col: 14 }, { row: 7, col: 14 },
      { row: 8, col: 14 },
      
      // Yellow start and path (positions 26-38)
      { row: 8, col: 13 }, { row: 8, col: 12 }, { row: 8, col: 11 },
      { row: 8, col: 10 }, { row: 8, col: 9 }, { row: 9, col: 8 },
      { row: 10, col: 8 }, { row: 11, col: 8 }, { row: 12, col: 8 },
      { row: 13, col: 8 }, { row: 14, col: 8 }, { row: 14, col: 7 },
      { row: 14, col: 6 },
      
      // Green start and path (positions 39-51)
      { row: 13, col: 6 }, { row: 12, col: 6 }, { row: 11, col: 6 },
      { row: 10, col: 6 }, { row: 9, col: 6 }, { row: 8, col: 5 },
      { row: 8, col: 4 }, { row: 8, col: 3 }, { row: 8, col: 2 },
      { row: 8, col: 1 }, { row: 8, col: 0 }, { row: 7, col: 0 },
      { row: 6, col: 0 }
    ];
    
    if (position >= 0 && position < pathCoordinates.length) {
      return pathCoordinates[position];
    }
    
    // For positions beyond the main path (safe zones 52-57)
    if (position >= 52) {
      // Safe zone mapping - all colors converge to center
      const safeZoneCoordinates = [
        { row: 7, col: 1 },  // Position 52 - first safe zone position
        { row: 7, col: 2 },  // Position 53
        { row: 7, col: 3 },  // Position 54
        { row: 7, col: 4 },  // Position 55
        { row: 7, col: 5 },  // Position 56
        { row: 7, col: 6 }   // Position 57 - finish line (just before center)
      ];
      
      const safeIndex = position - 52;
      if (safeIndex < safeZoneCoordinates.length) {
        return safeZoneCoordinates[safeIndex];
      }
      
      // Center for finished pieces
      return { row: 7, col: 7 };
    }
    
    // Default fallback
    return { row: 7, col: 7 };
  };

  const canMovePiece = (piece) => {
    return possibleMoves.some(move => move.pieceId === piece.id);
  };

  const handlePieceClick = (piece) => {
    console.group('🎯 Piece Click Debug');
    console.log('Piece clicked:', piece);
    console.log('Can move piece:', canMovePiece(piece));
    console.log('Current player:', currentPlayer);
    console.log('Player color:', playerColor);
    console.log('Dice value:', diceValue);
    console.log('Possible moves:', possibleMoves);
    
    const canMove = canMovePiece(piece);
    const isCurrentPlayer = currentPlayer && currentPlayer.color === playerColor;
    const hasDiceValue = !!diceValue;
    
    console.log('Conditions check:');
    console.log('- canMove:', canMove);
    console.log('- isCurrentPlayer:', isCurrentPlayer);
    console.log('- hasDiceValue:', hasDiceValue);
    
    if (canMove && isCurrentPlayer && hasDiceValue) {
      console.log('✅ All conditions met - Moving piece:', piece.id, 'with dice value:', diceValue);
      onMovePiece(piece.id, diceValue);
    } else {
      console.log('❌ Cannot move piece - conditions not met');
      if (!canMove) console.log('  - Piece not in possible moves');
      if (!isCurrentPlayer) console.log('  - Not current player turn');
      if (!hasDiceValue) console.log('  - No dice value');
    }
    console.groupEnd();
  };

  const getCellClassName = (cellInfo, row, col) => {
    let className = 'ludo-cell';
    
    switch (cellInfo.type) {
      case 'home':
        className += ` home-area ${cellInfo.color}-home`;
        break;
      case 'path':
        className += ' path-cell';
        break;
      case 'safe':
        className += ` safe-zone ${cellInfo.color}-safe`;
        break;
      case 'center':
        className += ' center-area';
        break;
      default:
        className += ' empty-cell';
    }
    
    return className;
  };

  const board = createBoard();

  return (
    <div className="ludo-board-container">
      <div className="ludo-board">
        {board.map((row, rowIndex) => (
          <div key={rowIndex} className="board-row">
            {row.map((cell, colIndex) => {
              const pieces = getPiecesAtPosition(rowIndex, colIndex);
              return (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className={getCellClassName(cell, rowIndex, colIndex)}
                >
                  {pieces.map((piece, pieceIndex) => (
                    <div
                      key={`${piece.id}-${pieceIndex}`}
                      className={`ludo-piece ${piece.playerColor}-piece ${canMovePiece(piece) ? 'moveable' : ''}`}
                      onClick={() => handlePieceClick(piece)}
                      title={`${piece.playerColor} piece ${piece.id}`}
                    >
                      ●
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      
      <div className="dice-area">
        {diceValue && (
          <div className="dice-display">
            <div className={`dice dice-${diceValue}`}>
              {diceValue}
            </div>
          </div>
        )}
        
        {/* Show Roll Dice button if it's current player's turn and no dice rolled yet or no possible moves */}
        {currentPlayer && currentPlayer.color === playerColor && 
         (!diceValue || (diceValue && possibleMoves.length === 0)) && (
          <button onClick={onRollDice} className="roll-dice-btn">
            🎲 Roll Dice
          </button>
        )}
        
        {currentPlayer && (
          <div className="current-turn">
            {currentPlayer.color === playerColor ? "Your turn!" : `${currentPlayer.name}'s turn`}
          </div>
        )}
      </div>
    </div>
  );
};

export default LudoBoard;
